import { logger } from '@/lib/logger';
import React, { useState, useEffect } from 'react';
import {
  PaymentElement,
  useStripe,
  useElements
} from '@stripe/react-stripe-js';
import { Button } from '@/components/ui/button';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { AlertTriangle, Tag, Mail } from 'lucide-react';
import { LoadingSpinner } from '@/components/LoadingSpinner'; // Adjust path as needed
import { useLocation } from 'react-router-dom';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { paymentApi } from '@/lib/payment-api';

interface StripePaymentFormProps {
  clientSecret: string; // Passed after creating PaymentIntent/Subscription
  onSuccess?: (paymentIntentId: string) => void; // Optional callback on success
  onError?: (error: any) => void; // Optional callback on error
  tier?: string; // Optional tier name for tracking subscription type
  isGuestCheckout?: boolean; // Whether this is a guest checkout (shows email input)
  guestEmail?: string; // Pre-filled guest email
  onGuestEmailChange?: (email: string) => void; // Callback when guest email changes
}

export const StripePaymentForm: React.FC<StripePaymentFormProps> = ({
  clientSecret,
  onSuccess,
  onError,
  tier,
  isGuestCheckout = false,
  guestEmail = '',
  onGuestEmailChange
}) => {
  const stripe = useStripe();
  const elements = useElements();
  const location = useLocation();

  const [email] = useState(''); // For LinkAuthenticationElement
  const [message, setMessage] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [couponCode, setCouponCode] = useState('');
  const [couponMessage, setCouponMessage] = useState<string | null>(null);
  const [isValidatingCoupon, setIsValidatingCoupon] = useState(false);
  const [validatedCoupon, setValidatedCoupon] = useState<string | null>(null);

  useEffect(() => {
    logger.debug('StripePaymentForm mounted with clientSecret:', { clientSecret: clientSecret ? `${clientSecret.substring(0, 8)}...` : 'none' });
    
    if (!stripe) {
      logger.warn('Stripe.js has not yet loaded');
      return;
    }

    if (!clientSecret) {
      logger.error('No client secret provided to StripePaymentForm');
      return;
    }

    // Check if this is a SetupIntent or PaymentIntent based on the client secret prefix
    const isSetupIntent = clientSecret.startsWith('seti_');
    
    if (isSetupIntent) {
      stripe.retrieveSetupIntent(clientSecret).then(({ setupIntent, error }) => {
        logger.debug('Initial SetupIntent status', { status: setupIntent?.status });
        if (error) {
          logger.error('Error retrieving setup intent', new Error(error.message));
          setMessage(`Error: ${error.message}`);
        } else if (setupIntent) {
          switch (setupIntent.status) {
            case 'succeeded':
              setMessage('Payment method saved successfully! Your subscription will be activated.');
              break;
            case 'processing':
              setMessage('Processing payment method setup...');
              break;
            case 'requires_payment_method':
              setMessage(null); // Let user enter payment details
              break;
            default:
              setMessage(null);
              break;
          }
        }
      });
    } else {
      // Handle PaymentIntent for immediate payment flows
      stripe.retrievePaymentIntent(clientSecret).then(({ paymentIntent, error }) => {
        logger.debug('Initial PaymentIntent status', { status: paymentIntent?.status });
        if (error) {
          logger.error('Error retrieving payment intent', new Error(error.message));
          setMessage(`Error: ${error.message}`);
        } else if (paymentIntent) {
          if (paymentIntent.status === 'succeeded') {
            setMessage('Payment succeeded! You will be redirected shortly.');
            if (onSuccess) onSuccess(paymentIntent.id);
          } else if (paymentIntent.status === 'requires_payment_method') {
            logger.debug('Payment requires payment method, ready for user input');
          } else {
            logger.debug(`Payment is in state: ${paymentIntent.status}`);
          }
        }
      }).catch(err => {
        logger.error('Exception retrieving payment intent:', err);
        setMessage(`Could not verify payment: ${err.message}`);
      });
    }
  }, [stripe, clientSecret, onSuccess]);

  const validateCoupon = async () => {
    if (!couponCode) return;
    
    setIsValidatingCoupon(true);
    setCouponMessage(null);
    
    try {
      // Call payment service to validate the coupon. Coupon input in this form is
      // gated to SetupIntent (subscription) flow, so productType is always 'subscription'.
      const { data, error } = await paymentApi(
        '/pay/validate-coupon',
        { couponCode, productType: 'subscription' },
        { auth: false }
      );

      if (error || !data) {
        setCouponMessage('Invalid or expired promo code');
        setValidatedCoupon(null);
        return;
      }

      if (data.valid) {
        // Use promotion_code_id if available (for promotion codes), otherwise coupon_id
        // This is what Stripe needs for subscription creation
        const codeToUse = data.promotion_code_id || data.coupon_id || couponCode;
        setValidatedCoupon(codeToUse);
        
        // Format the discount message
        let discountText = '';
        if (data.percent_off) {
          discountText = `${data.percent_off}% off`;
        } else if (data.amount_off) {
          discountText = `$${(data.amount_off / 100).toFixed(2)} off`;
        }
        
        if (data.duration === 'repeating') {
          discountText += ` for ${data.duration_in_months} months`;
        } else if (data.duration === 'forever') {
          discountText += ' forever';
        }
        
        setCouponMessage(`✓ Promo applied: ${discountText}`);
      } else {
        // Prefer server-provided reason (e.g., product-scope mismatch) over generic copy.
        setCouponMessage(data.message || 'Invalid or expired promo code');
        setValidatedCoupon(null);
      }
    } catch (err) {
      logger.error('Error validating coupon:', err);
      setCouponMessage('Unable to validate promo code');
      setValidatedCoupon(null);
    } finally {
      setIsValidatingCoupon(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!stripe || !elements) {
      // Stripe.js hasn't yet loaded.
      // Make sure to disable form submission until Stripe.js has loaded.
      logger.error('Stripe.js has not loaded yet.');
      setMessage('Stripe is not ready. Please wait a moment and try again.');
      return;
    }

    setIsLoading(true);
    setMessage(null); // Clear previous messages

    // Extract course ID from path if present (handles multiple URL patterns)
    let courseId = '';
    // Check for /courses/[id] pattern
    let pathMatch = location.pathname.match(/\/courses\/([^\/]+)/);
    if (!pathMatch) {
      // Check for /course-detail/[id] pattern
      pathMatch = location.pathname.match(/\/course-detail\/([^\/]+)/);
    }
    
    if (pathMatch && pathMatch[1]) {
      courseId = pathMatch[1];
    }

    // Build the return URL with courseId parameter if available
    let returnUrl = courseId 
      ? `${window.location.origin}/checkout-confirmation?course_id=${courseId}`
      : `${window.location.origin}/checkout-confirmation`;
    
    // Check if this is a SetupIntent or PaymentIntent based on the client secret prefix
    const isSetupIntent = clientSecret.startsWith('seti_');
    
    // Add tier parameter if available
    if (tier) {
      returnUrl += (returnUrl.includes('?') ? '&' : '?') + `tier=${encodeURIComponent(tier)}`;
    }
    
    // Add coupon parameter if available (for SetupIntent) - use validated coupon
    if (isSetupIntent && validatedCoupon) {
      returnUrl += (returnUrl.includes('?') ? '&' : '?') + `coupon=${encodeURIComponent(validatedCoupon)}`;
    }

    // Add guest checkout parameters
    if (isGuestCheckout && guestEmail) {
      returnUrl += (returnUrl.includes('?') ? '&' : '?') + `guest=true&email=${encodeURIComponent(guestEmail)}`;
    }

    logger.debug(`Confirming payment with return URL: ${returnUrl}`);

    try {
      
      // Store validated coupon code if provided (for subscription activation)
      if (isSetupIntent && validatedCoupon) {
        sessionStorage.setItem('pending_coupon', validatedCoupon);
        logger.debug('Stored validated coupon code for subscription activation:', { coupon: validatedCoupon });
      }
      
      let result;
      if (isSetupIntent) {
        // Confirm SetupIntent for saving payment method
        result = await stripe.confirmSetup({
          elements,
          confirmParams: {
            return_url: returnUrl,
          },
        });
      } else {
        // Confirm PaymentIntent for immediate payment
        result = await stripe.confirmPayment({
          elements,
          confirmParams: {
            // Make sure to change this to your payment completion page
            return_url: returnUrl,
            receipt_email: email || undefined, // Pass email if collected
          },
          // Uncomment below if you want to handle success/failure directly without redirecting
          // redirect: 'if_required'
        });
      }

      // This point will only be reached if there is an immediate error when
      // confirming the payment. Otherwise, your customer will be redirected to
      // your `return_url`. For some payment methods, your customer may be redirected
      // to an intermediate site first to authorize the payment, then redirected back.
      if (result.error) {
        logger.error('Stripe confirmPayment error', new Error(result.error.message));
        let userMessage = 'An unexpected error occurred.';
        if (result.error.type === "card_error" || result.error.type === "validation_error") {
            userMessage = (result.error as any).message || userMessage;
        } else {
            logger.error('Stripe confirmPayment error details', undefined, { errorDetails: JSON.stringify(result.error) });
            userMessage = "Payment failed. Please try again or contact support.";
        }
        setMessage(userMessage);
        if (onError) onError(result.error);
      }
      // If redirect: 'if_required' was used and payment succeeded directly:
      // else if (paymentIntent && paymentIntent.status === 'succeeded') {
      //    logger.debug('Payment succeeded directly!', paymentIntent);
      //    setMessage('Payment successful!');
      //    if(onSuccess) onSuccess(paymentIntent.id);
      // }
    } catch (err) {
      logger.error('Exception in Stripe confirm:', err);
      setMessage(`Payment error: ${err instanceof Error ? err.message : 'Unknown error'}`);
      if (onError) onError(err);
    } finally {
      setIsLoading(false);
    }
  };

  const paymentElementOptions = {
    layout: "tabs" as const // or "accordion"
  };

  // Email validation helper
  const isValidEmail = (email: string) => {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  };

  return (
    <form id="payment-form" onSubmit={handleSubmit} className="space-y-4">
      {/* Email input for guest checkout */}
      {isGuestCheckout && (
        <div className="space-y-2">
          <Label htmlFor="guest-email" className="text-sm font-medium">
            Email address
          </Label>
          <div className="relative">
            <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
            <Input
              id="guest-email"
              type="email"
              placeholder="you@example.com"
              value={guestEmail}
              onChange={(e) => onGuestEmailChange?.(e.target.value)}
              className="pl-10"
              disabled={isLoading}
              required
            />
          </div>
          <p className="text-xs text-gray-500">
            We'll create your account and send login instructions to this email.
          </p>
        </div>
      )}

      {/* Coupon Code Field - Only show for SetupIntent (subscriptions) */}
      {clientSecret.startsWith('seti_') && (
        <div className="space-y-2">
          <div className="flex gap-2">
            <div className="relative flex-1">
              <Tag className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
              <Input
                type="text"
                placeholder="Promo code (optional)"
                value={couponCode}
                onChange={(e) => {
                  setCouponCode(e.target.value.trim());
                  setCouponMessage(null);
                }}
                onKeyPress={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    validateCoupon();
                  }
                }}
                className="pl-10"
                disabled={isLoading}
              />
            </div>
            <Button
              type="button"
              variant="outline"
              onClick={validateCoupon}
              disabled={!couponCode || isLoading || isValidatingCoupon}
              className="px-6 bg-jdorange hover:bg-jdorange/90 text-white border-jdorange"
            >
              {isValidatingCoupon ? <LoadingSpinner className="h-4 w-4" /> : 'Apply'}
            </Button>
          </div>
          {couponMessage && (
            <p className={`text-sm ${couponMessage.includes('off') || couponMessage.includes('applied') ? 'text-green-600' : 'text-amber-600'}`}>
              {couponMessage}
            </p>
          )}
        </div>
      )}
      
      <PaymentElement id="payment-element" options={paymentElementOptions} />
      
      {message && (
        <Alert variant="destructive" className="dark:border-red-800 dark:bg-red-950/50">
          <AlertTriangle className="h-4 w-4" />
          <AlertTitle className="dark:text-red-300">Error</AlertTitle>
          <AlertDescription className="dark:text-red-300">{message}</AlertDescription>
        </Alert>
      )}

      <Button
        disabled={isLoading || !stripe || !elements || (isGuestCheckout && !isValidEmail(guestEmail))}
        id="submit"
        className="w-full bg-jdorange hover:bg-jdorange/90 dark:bg-jdorange dark:hover:bg-jdorange/90 dark:text-white"
      >
        <span id="button-text">
          {isLoading ? <LoadingSpinner className="h-5 w-5 inline mr-2" /> : "Pay now"}
        </span>
      </Button>
    </form>
  );
} 
